(() => {
  'use strict';

  const CONFIG_CACHE = new Map();
  const SCRIPT_CACHE = new Map();
  const PRELOAD_CACHE = new Set();
  const REVIEW_REQUESTS = new WeakMap();
  const PREPARED_DESIGNS = new WeakMap();
  const REVIEW_COMMIT_STATES = new WeakMap();
  const READY_DESIGN_REQUESTS = new WeakMap();
  const READY_DESIGN_EXPIRIES = new WeakMap();
  const LIVE_QUOTE_STATES = new WeakMap();
  const DESIGN_SCHEMA_VERSION = 1;
  const DEFAULT_PROXY_BASE = '/apps/tan-designer';
  const LIVE_QUOTE_DEBOUNCE_MS = 300;
  const LIVE_QUOTE_EXPIRY_SAFETY_MS = 5_000;
  const LIVE_QUOTE_CACHE_LIMIT = 24;
  const READY_DESIGN_REQUEST_LIMIT = 8;
  const READY_DESIGN_QUOTE_TTL_MS = 30_000;
  const SIZE_QUOTE_INPUT_SELECTOR = '[data-width-range]';
  const DISCRETE_QUOTE_COALESCE_MS = 75;
  const DISCRETE_QUOTE_COMMIT_TTL_MS = 15_000;
  const DISCRETE_QUOTE_CLICK_SELECTOR = [
    '[data-font-option]',
    '[data-icon-option]',
    '[data-character-color]',
    '[data-mobile-alignment]',
  ].join(', ');
  const DISCRETE_QUOTE_CHANGE_SELECTOR = [
    '[data-alignment]',
    '[data-color-mode]',
    '[data-color]',
    '[data-backboard-color]',
    'input[name$="-usage"]',
    'input[name$="-power-adapter"]',
    'input[name$="-backboard"]',
    'input[name$="-mounting"]',
    'input[name$="-remote"]',
    'input[name$="-rush"]',
    'input[name$="-mockup"]',
    'input[name$="-greeting"]',
  ].join(', ');
  const CONTINUOUS_QUOTE_INPUT_SELECTOR = [
    '[data-text-input]',
    '[data-width-range]',
    '[data-width-input]',
    '[data-height-input]',
    '[data-icon-placement]',
    '[data-icon-scale]',
    '[data-rush-input]',
    '[data-greeting-input]',
    '[data-special-request-input]',
  ].join(', ');
  const SIZE_COMMIT_KEYS = new Set([
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'End',
    'Home',
    'PageDown',
    'PageUp',
  ]);
  const CART_PROGRESS_STEPS = ['prepare', 'cart', 'preview'];
  const CART_PROGRESS_MIN_DESKTOP_PHASE_MS = 650;
  const COPY_CACHE = new WeakMap();

  const DEFAULT_COPY = Object.freeze({
    'summary.add_to_cart': 'Add to cart',
    'summary.exact_total': 'Exact total [[price]]',
    'progress.title': 'Preparing your custom sign',
    'progress.description': 'Please keep this page open while we prepare your design.',
    'progress.preparing': 'Generating image and creating product',
    'progress.preparing_detail': 'Creating your product image and secure custom item.',
    'progress.adding': 'Adding to cart',
    'progress.adding_detail': 'Saving your selected design details.',
    'progress.finalizing': 'Finalizing cart preview',
    'progress.finalizing_detail': 'Making sure your product image is ready.',
    'progress.complete': 'Your custom sign is ready in the cart.',
    'progress.close': 'Close',
    'errors.secure_service_unavailable':
      'The secure designer service is unavailable. Refresh the page and try again.',
    'errors.secure_checkout_unavailable': 'Secure checkout is temporarily unavailable.',
    'errors.interface_load_failed': 'The designer interface could not be loaded.',
    'errors.script_url_missing': 'The designer script URL is missing.',
    'errors.font_catalog_container_missing': 'The designer font catalog container is missing.',
    'errors.font_catalog_empty': 'The app font catalog has no active fonts.',
    'errors.font_catalog_revision_missing': 'The app font catalog revision is missing.',
    'errors.exact_price_failed': 'The exact design price could not be confirmed.',
    'errors.exact_price_invalid': 'The exact design price is invalid.',
    'errors.price_changed':
      'The design changed while its price was being confirmed. Review it again.',
    'errors.incomplete_selections': 'Complete the required design selections before continuing.',
    'errors.select_background_continue':
      'Select the Cart and product image background in this section’s theme-editor settings before continuing.',
    'errors.prepare_product_failed': 'The design product could not be prepared. Try again.',
    'errors.product_still_preparing':
      'The design product is still being prepared. Try adding it again in a moment.',
    'errors.design_changed':
      'The design changed before it could be added. Review the latest design and try again.',
    'errors.invalid_variant': 'Shopify returned an invalid design variant.',
    'errors.add_to_cart_failed': 'The custom sign could not be added to the cart.',
  });

  const SERVICE_ERROR_COPY_KEYS = Object.freeze({
    CATALOG_CHANGED: 'errors.design_changed',
    FONT_UNAVAILABLE: 'errors.incomplete_selections',
    ICON_UNAVAILABLE: 'errors.incomplete_selections',
    IDEMPOTENCY_CONFLICT: 'errors.design_changed',
    IDEMPOTENCY_IN_PROGRESS: 'errors.product_still_preparing',
    NOT_FOUND: 'errors.prepare_product_failed',
    RATE_LIMITED: 'errors.secure_service_unavailable',
    SERVICE_NOT_CONFIGURED: 'errors.secure_service_unavailable',
    UNAUTHENTICATED: 'errors.secure_service_unavailable',
    UNSUPPORTED_MEDIA_TYPE: 'errors.incomplete_selections',
    BODY_TOO_LARGE: 'errors.incomplete_selections',
    EMPTY_BODY: 'errors.incomplete_selections',
    INVALID_ENCODING: 'errors.incomplete_selections',
    INVALID_JSON: 'errors.incomplete_selections',
    INVALID_IDEMPOTENCY_KEY: 'errors.add_to_cart_failed',
    METHOD_NOT_ALLOWED: 'errors.secure_service_unavailable',
    INTERNAL_ERROR: 'errors.secure_service_unavailable',
    INVALID_COUNTRY: 'errors.secure_service_unavailable',
  });

  const PROGRESS_COPY_KEYS = Object.freeze({
    prepare: ['progress.preparing', 'progress.preparing_detail'],
    cart: ['progress.adding', 'progress.adding_detail'],
    preview: ['progress.finalizing', 'progress.finalizing_detail'],
  });

  function copyTable(root) {
    if (COPY_CACHE.has(root)) return COPY_CACHE.get(root);
    const table = Object.create(null);
    const node = root.querySelector('[data-tan-nd-copy]');
    try {
      const parsed = JSON.parse(String(node?.textContent || '{}'));
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        Object.keys(DEFAULT_COPY).forEach((key) => {
          const value = parsed[key];
          if (typeof value === 'string' && value.length <= 4000) table[key] = value;
        });
      }
    } catch {
      // Invalid or missing locale JSON fails safely to the checked-in English copy below.
    }
    COPY_CACHE.set(root, table);
    return table;
  }

  function copy(root, key, tokens = {}) {
    const table = copyTable(root);
    const template = table[key] || DEFAULT_COPY[key] || '';
    return template.replace(/\[\[([a-z][a-z0-9_]*)\]\]/gi, (_match, token) =>
      Object.prototype.hasOwnProperty.call(tokens, token) ? String(tokens[token]) : ''
    );
  }

  function bridgeError(copyKey, code = 'DESIGNER_BRIDGE_ERROR', tokens = {}) {
    const error = new Error(code);
    error.code = code;
    error.copyKey = copyKey;
    error.copyTokens = tokens;
    return error;
  }

  function serviceErrorCode(payload) {
    const code = String(payload?.error?.code || '').trim();
    return /^[A-Z][A-Z0-9_]{2,63}$/.test(code) ? code : 'DESIGNER_SERVICE_ERROR';
  }

  function customerMessage(root, error, fallbackKey) {
    const requestedKey =
      error && typeof error.copyKey === 'string' && DEFAULT_COPY[error.copyKey]
        ? error.copyKey
        : '';
    const code = error && typeof error.code === 'string' ? error.code : '';
    const key = requestedKey || SERVICE_ERROR_COPY_KEYS[code] || fallbackKey;
    const tokens = error && error.copyTokens && typeof error.copyTokens === 'object'
      ? error.copyTokens
      : {};
    return copy(root, key, tokens);
  }

  function customerError(root, error, fallbackKey) {
    const safe = new Error(customerMessage(root, error, fallbackKey));
    safe.code = error && typeof error.code === 'string' ? error.code : 'DESIGNER_BRIDGE_ERROR';
    return safe;
  }

  const COLOR_IDS = new Map(
    Object.entries({
      '#ff0099': 'rose-red',
      '#f4ddb9': 'warm-white',
      '#e8f4ff': 'cool-white',
      '#ff6600': 'orange',
      '#9d00ff': 'purple',
      '#ffd500': 'yellow',
      '#f4b942': 'gold',
      '#00f030': 'green',
      '#ff0000': 'red',
      '#ff00ff': 'pink',
      '#00ffff': 'ice-blue',
      '#0033ff': 'blue',
      'rose red': 'rose-red',
      'warm white': 'warm-white',
      'cool white': 'cool-white',
      orange: 'orange',
      purple: 'purple',
      yellow: 'yellow',
      gold: 'gold',
      green: 'green',
      red: 'red',
      pink: 'pink',
      'ice blue': 'ice-blue',
      blue: 'blue',
    })
  );

  const BACKBOARD_STYLES = new Map([
    ['cut to shape', 'cut'],
    ['cut to letter', 'cut-letter'],
    ['rectangular board', 'rectangle'],
    ['acrylic stand', 'stand'],
    ['box style', 'box'],
  ]);
  const BACKBOARD_COLORS = new Map([
    ['clear acrylic', 'clear'],
    ['black acrylic', 'black'],
    ['white acrylic', 'white'],
    ['silver mirror acrylic', 'silver-mirror'],
    ['gold mirror acrylic', 'gold-mirror'],
  ]);
  const MOUNTING = new Map([
    ['wall mount kit', 'wall-mount-kit'],
    ['hanging kit', 'hanging-kit'],
    ['3m strips', '3m-strips'],
  ]);

  function normalized(value) {
    return String(value || '').trim().toLocaleLowerCase('en');
  }

  function absoluteHttpsUrl(value) {
    try {
      const url = new URL(String(value || ''), window.location.origin);
      return url.protocol === 'https:' ? url.href : '';
    } catch {
      return '';
    }
  }

  function mapped(table, value, fallback) {
    return table.get(normalized(value)) || fallback;
  }

  function colorId(value) {
    return COLOR_IDS.get(normalized(value)) || 'rose-red';
  }

  function proxyBase(root) {
    const raw = String(root.dataset.appProxyBase || DEFAULT_PROXY_BASE).trim();
    return `/${raw.replace(/^\/+|\/+$/g, '')}`;
  }

  function showError(root, message) {
    root.querySelectorAll('[data-cart-error]').forEach((output) => {
      if (!(output instanceof HTMLElement)) return;
      output.textContent = String(message || '');
      output.hidden = !message;
    });
  }

  function cartProgressDialog(root) {
    const dialog = root.querySelector('[data-cart-progress-dialog]');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  function setCartProgress(root, activeStep) {
    const dialog = cartProgressDialog(root);
    if (!dialog) return;
    if (dialog.dataset.progressStep !== activeStep) {
      dialog.dataset.progressStep = activeStep;
      dialog.dataset.progressStepStartedAt = String(progressNow());
    }
    const activeIndex = CART_PROGRESS_STEPS.indexOf(activeStep);
    dialog.querySelectorAll('[data-cart-progress-step]').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      const step = String(item.dataset.cartProgressStep || '');
      const index = CART_PROGRESS_STEPS.indexOf(step);
      const status = index < activeIndex ? 'complete' : index === activeIndex ? 'active' : 'pending';
      item.dataset.status = status;
      if (status === 'active') item.setAttribute('aria-current', 'step');
      else item.removeAttribute('aria-current');
      const copyKeys = PROGRESS_COPY_KEYS[step];
      const heading = item.querySelector('strong');
      const detail = item.querySelector('small');
      if (copyKeys && heading instanceof HTMLElement) heading.textContent = copy(root, copyKeys[0]);
      if (copyKeys && detail instanceof HTMLElement) detail.textContent = copy(root, copyKeys[1]);
    });
    const active = dialog.querySelector(`[data-cart-progress-step="${activeStep}"] strong`);
    const live = dialog.querySelector('[data-cart-progress-live]');
    if (live instanceof HTMLElement && active) live.textContent = active.textContent || '';
  }

  function progressNow() {
    return typeof window.performance?.now === 'function' ? window.performance.now() : Date.now();
  }

  function desktopProgressMinimum() {
    if (typeof window.matchMedia !== 'function') return 0;
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches
      ? CART_PROGRESS_MIN_DESKTOP_PHASE_MS
      : 0;
  }

  async function holdActiveCartProgress(root) {
    const dialog = cartProgressDialog(root);
    const minimum = desktopProgressMinimum();
    if (!dialog || minimum <= 0 || dialog.dataset.progressState !== 'busy') return;
    const startedAt = Number(dialog.dataset.progressStepStartedAt);
    if (!Number.isFinite(startedAt)) return;
    const remaining = Math.max(0, minimum - (progressNow() - startedAt));
    if (remaining > 0) {
      await new Promise((resolve) => window.setTimeout(resolve, remaining));
    }
  }

  function openCartProgress(root) {
    const dialog = cartProgressDialog(root);
    if (!dialog) return;
    const error = dialog.querySelector('[data-cart-progress-error]');
    const close = dialog.querySelector('[data-cart-progress-close]');
    if (error instanceof HTMLElement) {
      error.textContent = '';
      error.hidden = true;
    }
    if (close instanceof HTMLButtonElement) close.hidden = true;
    dialog.dataset.progressState = 'busy';
    dialog.setAttribute('aria-busy', 'true');
    delete dialog.dataset.progressStep;
    delete dialog.dataset.progressStepStartedAt;
    setCartProgress(root, 'prepare');
    if (!dialog.open) dialog.showModal();
    window.requestAnimationFrame(() => dialog.focus({ preventScroll: true }));
  }

  function completeCartProgress(root) {
    const dialog = cartProgressDialog(root);
    if (!dialog) return;
    dialog.querySelectorAll('[data-cart-progress-step]').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      item.dataset.status = 'complete';
      item.removeAttribute('aria-current');
    });
    dialog.dataset.progressState = 'complete';
    dialog.setAttribute('aria-busy', 'false');
    const live = dialog.querySelector('[data-cart-progress-live]');
    if (live instanceof HTMLElement) {
      live.textContent = copy(root, 'progress.complete');
    }
  }

  function failCartProgress(root, message) {
    const dialog = cartProgressDialog(root);
    if (!dialog) return;
    const active = dialog.querySelector('[data-cart-progress-step][data-status="active"]');
    if (active instanceof HTMLElement) {
      active.dataset.status = 'error';
      active.removeAttribute('aria-current');
    }
    dialog.dataset.progressState = 'error';
    dialog.setAttribute('aria-busy', 'false');
    const error = dialog.querySelector('[data-cart-progress-error]');
    const live = dialog.querySelector('[data-cart-progress-live]');
    const close = dialog.querySelector('[data-cart-progress-close]');
    if (error instanceof HTMLElement) {
      error.textContent = String(message || copy(root, 'errors.add_to_cart_failed'));
      error.hidden = false;
    }
    if (live instanceof HTMLElement) live.textContent = String(message || '');
    if (close instanceof HTMLButtonElement) {
      close.hidden = false;
      window.requestAnimationFrame(() => close.focus({ preventScroll: true }));
    }
  }

  function installCartProgress(root) {
    const dialog = cartProgressDialog(root);
    if (!dialog || dialog.dataset.progressInitialized === 'true') return;
    dialog.dataset.progressInitialized = 'true';
    const title = dialog.querySelector('header h2');
    const description = dialog.querySelector('header p');
    const close = dialog.querySelector('[data-cart-progress-close]');
    if (title instanceof HTMLElement) title.textContent = copy(root, 'progress.title');
    if (description instanceof HTMLElement) description.textContent = copy(root, 'progress.description');
    if (close instanceof HTMLButtonElement) {
      close.textContent = copy(root, 'progress.close');
      close.addEventListener('click', () => dialog.close());
    }
    dialog.addEventListener('cancel', (event) => {
      if (dialog.dataset.progressState === 'busy') event.preventDefault();
    });
    dialog.addEventListener('close', () => {
      const button = root.querySelector('[data-add-to-cart]');
      if (button instanceof HTMLButtonElement) button.focus({ preventScroll: true });
    });
    window.addEventListener('pageshow', () => {
      if (!root.isConnected) return;
      if (dialog.open) dialog.close();
      setLoading(root, false);
    });
  }

  function buyerCountryCode(root) {
    const countryCode = String(root.dataset.countryCode || '')
      .trim()
      .toUpperCase();
    return /^[A-Z]{2}$/.test(countryCode) ? countryCode : '';
  }

  function contextualApiUrl(root, path) {
    const endpoint = `${proxyBase(root)}/${path.replace(/^\/+/, '')}`;
    const countryCode = buyerCountryCode(root);
    if (!countryCode) return endpoint;
    const separator = endpoint.includes('?') ? '&' : '?';
    return `${endpoint}${separator}country_code=${encodeURIComponent(countryCode)}`;
  }

  async function apiRequest(root, path, options = {}) {
    const response = await fetch(contextualApiUrl(root, path), {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
      },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error('DESIGNER_SERVICE_ERROR');
      error.code = serviceErrorCode(payload);
      error.status = response.status;
      throw error;
    }
    return payload.data;
  }

  function configFor(root) {
    const base = proxyBase(root);
    if (!CONFIG_CACHE.has(base)) {
      CONFIG_CACHE.set(base, apiRequest(root, 'config').catch((error) => {
        CONFIG_CACHE.delete(base);
        throw error;
      }));
    }
    return CONFIG_CACHE.get(base);
  }

  function installCatalog(root, config) {
    const catalogNode = root.querySelector('[data-tan-nd-fonts]');
    if (!(catalogNode instanceof HTMLScriptElement)) {
      throw bridgeError('errors.font_catalog_container_missing', 'FONT_CATALOG_CONTAINER_MISSING');
    }
    const fonts = Array.isArray(config?.catalog?.fonts)
      ? config.catalog.fonts.filter((font) => font?.status === 'active')
      : [];
    if (!fonts.length) throw bridgeError('errors.font_catalog_empty', 'FONT_CATALOG_EMPTY');
    catalogNode.textContent = JSON.stringify(fonts);
    root.dataset.catalogRevision = String(config.catalogRevision || '');
    if (!root.dataset.catalogRevision) {
      throw bridgeError('errors.font_catalog_revision_missing', 'FONT_CATALOG_REVISION_MISSING');
    }
    root.dataset.appBackendReady = 'true';
    root.querySelectorAll('[data-legacy-product-warning]').forEach((warning) => {
      if (warning instanceof HTMLElement) warning.hidden = true;
    });
    return fonts;
  }

  function appendPreload(value, as, type = '') {
    const raw = String(value || '').trim();
    if (!raw) return;
    const source = absoluteHttpsUrl(raw);
    const key = `${as}:${source}`;
    if (!source || PRELOAD_CACHE.has(key) || !document.head) return;
    try {
      const existing = Array.from(document.head.querySelectorAll('link[rel="preload"]')).some(
        (link) => link instanceof HTMLLinkElement && link.as === as && link.href === source
      );
      if (existing) {
        PRELOAD_CACHE.add(key);
        return;
      }
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = as;
      link.href = source;
      if (type) link.type = type;
      if (as === 'font') link.crossOrigin = 'anonymous';
      document.head.append(link);
      PRELOAD_CACHE.add(key);
    } catch {
      return;
    }
  }

  function preloadDesignerScript(root) {
    const source = String(root.dataset.designerScriptUrl || '').trim();
    if (!source || customElements.get('tan-neon-designer') || SCRIPT_CACHE.has(source)) return;
    appendPreload(source, 'script');
  }

  function preloadInitialCatalogFont(fonts) {
    const font =
      fonts.find(
        (entry) =>
          String(entry?.id || '') === 'catalog-barcelona' ||
          normalized(entry?.label) === 'barcelona'
      ) || fonts[0];
    const source = absoluteHttpsUrl(font?.url);
    if (!source) return;
    try {
      const url = new URL(source);
      if (
        url.hostname !== 'cdn.shopify.com' ||
        !url.pathname.toLocaleLowerCase('en').endsWith('.woff2')
      ) {
        return;
      }
      appendPreload(url.href, 'font', 'font/woff2');
    } catch {
      return;
    }
  }

  function loadDesignerScript(root) {
    const source = String(root.dataset.designerScriptUrl || '').trim();
    if (!source) {
      return Promise.reject(bridgeError('errors.script_url_missing', 'DESIGNER_SCRIPT_URL_MISSING'));
    }
    if (customElements.get('tan-neon-designer')) return Promise.resolve();
    if (!SCRIPT_CACHE.has(source)) {
      SCRIPT_CACHE.set(
        source,
        new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = source;
          script.async = true;
          script.addEventListener('load', resolve, { once: true });
          script.addEventListener(
            'error',
            () => reject(bridgeError('errors.interface_load_failed', 'DESIGNER_INTERFACE_LOAD_FAILED')),
            { once: true }
          );
          document.head.append(script);
        }).catch((error) => {
          SCRIPT_CACHE.delete(source);
          throw error;
        })
      );
    }
    return SCRIPT_CACHE.get(source);
  }

  async function bootstrapRoot(root) {
    if (!(root instanceof HTMLElement) || root.dataset.appBridgeInitialized === 'true') return;
    root.dataset.appBridgeInitialized = 'true';
    installLiveQuoteListener(root);
    installCartProgress(root);
    // The theme designer is loaded below. Mask its shop-base price before its
    // first render so only the buyer-country quote is ever presented.
    setExactQuoteStatus(root, 'pending');
    try {
      const configRequest = configFor(root);
      preloadDesignerScript(root);
      const fonts = installCatalog(root, await configRequest);
      preloadInitialCatalogFont(fonts);
      showError(root, '');
    } catch (error) {
      root.dataset.appBackendReady = 'false';
      setExactQuoteStatus(root, 'error');
      showError(root, customerMessage(root, error, 'errors.secure_checkout_unavailable'));
    }
    try {
      await loadDesignerScript(root);
    } catch (error) {
      setExactQuoteStatus(root, 'error');
      showError(root, customerMessage(root, error, 'errors.interface_load_failed'));
    }
    // The listener is installed before the designer resumes. Its first normal
    // render therefore becomes the initial live quote without a second
    // bootstrap-only state path.
    root.dispatchEvent(new CustomEvent('tan:app-bridge:ready'));
  }

  function canonicalDesign(root, state) {
    if (!state || typeof state !== 'object' || !state.valid) {
      throw bridgeError('errors.incomplete_selections', 'INCOMPLETE_DESIGN');
    }
    if (root.dataset.appBackendReady !== 'true' || !root.dataset.catalogRevision) {
      throw bridgeError('errors.secure_service_unavailable', 'DESIGNER_SERVICE_UNAVAILABLE');
    }

    const mode = ['solid', 'rgb', 'combination'].includes(state.colorMode)
      ? state.colorMode
      : 'solid';
    const iconPosition = ['left', 'right', 'between'].includes(state.iconPosition)
      ? state.iconPosition
      : 'right';
    const usageId = normalized(state.usage).startsWith('outdoor')
      ? 'outdoor'
      : 'indoor';
    const powerAdapterId =
      usageId === 'outdoor' && state.powerAdapterId === 'outdoor-rated'
        ? 'outdoor-rated'
        : 'standard-indoor';
    const icon = state.icon
      ? {
          id: String(state.iconId || ''),
          position: iconPosition,
          placement: Math.max(0, Math.min(100, Math.round(Number(state.iconPlacement) || 0))),
          scalePercent: Math.max(70, Math.min(160, Number(state.iconScale) || 100)),
          ...(mode === 'combination'
            ? { colorId: colorId(state.iconColorId || state.colorId) }
            : {}),
        }
      : null;
    const backgroundUrl = absoluteHttpsUrl(root.dataset.productPreviewBackgroundUrl);
    if (!backgroundUrl) {
      throw bridgeError('errors.select_background_continue', 'PRODUCT_PREVIEW_BACKGROUND_MISSING');
    }
    const background = {
      id: 'product-preview',
      label: 'Product preview background',
      url: backgroundUrl,
    };

    return {
      schemaVersion: DESIGN_SCHEMA_VERSION,
      catalogRevision: String(root.dataset.catalogRevision),
      fontId: String(state.font?.id || ''),
      text: String(state.text || ''),
      size: {
        driver: state.sizeDriver === 'height' ? 'height' : 'width',
        requestedWidthCm: Number(state.width),
        requestedHeightCm: Number(state.height),
      },
      options: {
        alignment: ['left', 'center', 'right'].includes(state.alignment) ? state.alignment : 'center',
        usageId,
        powerAdapterId,
        colorMode: mode,
        colorId: colorId(state.colorId),
        characterColorIds:
          mode === 'combination'
            ? (Array.isArray(state.characterColors) ? state.characterColors : []).map(colorId)
            : [],
        backboardStyleId: mapped(BACKBOARD_STYLES, state.backboard, 'cut'),
        backboardColorId: mapped(BACKBOARD_COLORS, state.backboardColor, 'clear'),
        mountingId: mapped(MOUNTING, state.mounting, 'wall-mount-kit'),
        remoteId:
          mode === 'rgb'
            ? 'included'
            : normalized(state.remote) === 'no'
              ? 'none'
              : 'included',
        productionId: normalized(state.rush) === 'rush my order' ? 'rush' : 'standard',
        mockupId:
          normalized(state.mockup) === 'send a mock-up before production' ? 'approval' : 'proceed',
        greetingCardId: normalized(state.greeting) === 'yes' ? 'yes' : 'none',
        icon,
      },
      preview: {
        offsetX: Math.max(-1, Math.min(1, Number(state.previewOffset?.x) || 0)),
        offsetY: Math.max(-1, Math.min(1, Number(state.previewOffset?.y) || 0)),
        background,
      },
      notes: {
        rush: String(state.rushNotes || ''),
        greetingCard: String(state.greetingMessage || ''),
        specialRequest: String(state.specialRequest || ''),
      },
    };
  }

  function liveQuoteState(root) {
    if (!LIVE_QUOTE_STATES.has(root)) {
      LIVE_QUOTE_STATES.set(root, {
        timer: 0,
        currentKey: '',
        currentRevision: '',
        currentState: null,
        currentQuote: null,
        lastDisplayedQuote: null,
        requestSequence: 0,
        hasRequestedQuote: false,
        sizeInteractionActive: false,
        sizeInteractionDirty: false,
        sizeInteractionKind: '',
        sizeCommitPending: false,
        sizeCommitFrame: 0,
        deferredSizeQuote: null,
        discreteQuoteCommitPending: false,
        discreteQuoteCommitSequence: 0,
        discreteQuoteCommitTimer: 0,
        inFlight: new Map(),
        cache: new Map(),
      });
    }
    return LIVE_QUOTE_STATES.get(root);
  }

  function syncExactQuoteDisplay(root, status) {
    const waiting = status === 'pending';
    const masked = root.dataset.hasExactQuote !== 'true';
    root
      .querySelectorAll('[data-price-output], [data-canvas-price-output], [data-review-price]')
      .forEach((output) => {
        if (!(output instanceof HTMLElement)) return;
        if (waiting) output.setAttribute('aria-busy', 'true');
        else output.removeAttribute('aria-busy');
        output.style.transition = 'opacity 120ms ease';
        output.style.opacity = masked ? '0' : '1';
        output.style.visibility = masked ? 'hidden' : '';
      });
    root
      .querySelectorAll('[data-list-price], [data-canvas-list-price], [data-review-list-price]')
      .forEach((output) => {
        if (!(output instanceof HTMLElement)) return;
        output.style.transition = 'opacity 120ms ease';
        output.style.opacity = masked ? '0' : '1';
        output.style.visibility = masked ? 'hidden' : '';
      });
    const sizeOutput = root.querySelector('[data-canvas-size-price]');
    if (sizeOutput instanceof HTMLElement) {
      if (waiting) sizeOutput.setAttribute('aria-busy', 'true');
      else sizeOutput.removeAttribute('aria-busy');
      sizeOutput.style.transition = 'opacity 120ms ease';
      sizeOutput.style.opacity = masked ? '0' : '1';
      sizeOutput.style.visibility = masked ? 'hidden' : '';
    }
  }

  function setExactQuoteStatus(root, status) {
    if (typeof root.setExactQuoteStatus === 'function') {
      root.setExactQuoteStatus(status);
    }
    root.dataset.exactQuoteStatus = status;
    syncExactQuoteDisplay(root, status);
  }

  function clearLiveQuoteTimer(context) {
    if (!context.timer) return;
    if (typeof window.clearTimeout === 'function') {
      window.clearTimeout(context.timer);
    }
    context.timer = 0;
  }

  function canonicalKey(root, design) {
    return JSON.stringify({ countryCode: buyerCountryCode(root), design });
  }

  function quoteExpiresAt(quote) {
    const parsed = Date.parse(String(quote?.expiresAt || ''));
    // Production quotes always carry their persistent expiry. Retain a very
    // short compatibility window for an older proxy response without it.
    return Number.isFinite(parsed) ? parsed : Date.now() + 30_000;
  }

  function quoteIsFresh(entry) {
    return Boolean(
      entry &&
        entry.quote &&
        Number(entry.expiresAt) > Date.now() + LIVE_QUOTE_EXPIRY_SAFETY_MS
    );
  }

  function cachedQuote(context, key) {
    const entry = context.cache.get(key);
    if (!quoteIsFresh(entry)) {
      if (entry) context.cache.delete(key);
      return null;
    }
    // Refresh insertion order so the small per-root cache behaves as an LRU.
    context.cache.delete(key);
    context.cache.set(key, entry);
    return entry.quote;
  }

  function cacheQuote(context, keys, quote) {
    const entry = { quote, expiresAt: quoteExpiresAt(quote) };
    keys.forEach((key) => {
      if (!key) return;
      context.cache.delete(key);
      context.cache.set(key, entry);
    });
    while (context.cache.size > LIVE_QUOTE_CACHE_LIMIT) {
      context.cache.delete(context.cache.keys().next().value);
    }
  }

  function resolvedCanonicalKey(root, design, quote) {
    const size = quoteResolvedSize(quote);
    return canonicalKey(root, {
      ...design,
      size: {
        ...design.size,
        requestedWidthCm: size.widthCm,
        requestedHeightCm: size.heightCm,
      },
    });
  }

  function isCurrentQuoteTarget(context, key, revision) {
    return (
      context.currentKey === key &&
      context.currentRevision === String(revision)
    );
  }

  function markQuoteTarget(root, context, state, key, revision) {
    clearLiveQuoteTimer(context);
    context.currentKey = key;
    context.currentRevision = String(revision);
    context.currentState = state;
    context.currentQuote = null;
    context.requestSequence += 1;
    if (context.lastDisplayedQuote) {
      renderExactPriceOutputs(root, context.lastDisplayedQuote);
    }
    setExactQuoteStatus(root, 'pending');
    showError(root, '');
  }

  function rememberExactPriceDisplay(root, context, quote) {
    context.lastDisplayedQuote = quote;
    root.dataset.hasExactQuote = 'true';
  }

  function applyCurrentQuote(root, context, state, design, key, revision, quote) {
    if (!isCurrentQuoteTarget(context, key, revision)) return false;
    const resolvedKey = resolvedCanonicalKey(root, design, quote);
    if (!applyAuthoritativeQuote(root, state, quote, revision)) return false;
    rememberExactPriceDisplay(root, context, quote);
    cacheQuote(context, [key, resolvedKey], quote);
    context.currentKey = resolvedKey;
    context.currentState = state;
    context.currentQuote = {
      quote,
      keys: new Set([key, resolvedKey]),
      revision: String(revision),
      expiresAt: quoteExpiresAt(quote),
    };
    setExactQuoteStatus(root, 'ready');
    showError(root, '');
    return true;
  }

  function requestQuote(root, context, design, key) {
    const existing = context.inFlight.get(key);
    if (existing) return existing;
    context.hasRequestedQuote = true;
    const pending = apiRequest(root, 'quotes', {
      method: 'POST',
      body: JSON.stringify(design),
    }).finally(() => {
      if (context.inFlight.get(key) === pending) {
        context.inFlight.delete(key);
      }
    });
    context.inFlight.set(key, pending);
    return pending;
  }

  async function resolveQuoteTarget(root, state, design, key, revision) {
    const context = liveQuoteState(root);
    const cached = cachedQuote(context, key);
    if (cached) {
      applyCurrentQuote(root, context, state, design, key, revision, cached);
      return cached;
    }

    try {
      const quote = await requestQuote(root, context, design, key);
      // Cache only after the complete country, money, and resolved-size quote
      // contract has been validated while applying to the current target.
      // A stale response is intentionally discarded instead of allowing an
      // unvalidated service response to poison later in-memory cache hits.
      applyCurrentQuote(root, context, state, design, key, revision, quote);
      return quote;
    } catch (error) {
      if (isCurrentQuoteTarget(context, key, revision)) {
        setExactQuoteStatus(root, 'error');
        showError(root, customerMessage(root, error, 'errors.exact_price_failed'));
      }
      throw error;
    }
  }

  function scheduleLiveQuote(root, state, revision, options = {}) {
    const context = liveQuoteState(root);
    clearLiveQuoteTimer(context);
    const immediate = options.immediate === true;
    const deferRequest = options.deferRequest === true;
    const delayMs = Number.isFinite(options.delayMs)
      ? Math.max(0, Number(options.delayMs))
      : LIVE_QUOTE_DEBOUNCE_MS;

    let design;
    try {
      design = canonicalDesign(root, state);
    } catch (error) {
      context.currentKey = '';
      context.currentRevision = String(revision);
      context.currentState = state;
      context.currentQuote = null;
      context.deferredSizeQuote = deferRequest
        ? { state, revision: String(revision) }
        : null;
      if (context.lastDisplayedQuote) {
        renderExactPriceOutputs(root, context.lastDisplayedQuote);
      }
      if (state && state.valid) {
        setExactQuoteStatus(root, 'error');
        showError(root, customerMessage(root, error, 'errors.exact_price_failed'));
      } else {
        setExactQuoteStatus(root, 'idle');
        showError(root, '');
      }
      return;
    }

    const key = canonicalKey(root, design);
    markQuoteTarget(root, context, state, key, revision);
    if (deferRequest) {
      context.deferredSizeQuote = { state, revision: String(revision) };
      return;
    }
    context.deferredSizeQuote = null;
    const cached = cachedQuote(context, key);
    if (cached) {
      applyCurrentQuote(root, context, state, design, key, revision, cached);
      return;
    }

    const requestCurrentQuote = () => {
      context.timer = 0;
      void resolveQuoteTarget(root, state, design, key, revision).catch(() => {
        // Current errors were localized above; stale errors are intentionally silent.
      });
    };
    if (immediate || !context.hasRequestedQuote) {
      requestCurrentQuote();
      return;
    }
    context.timer = window.setTimeout(requestCurrentQuote, delayMs);
  }

  function trustedSizeInput(root, event) {
    if (!event?.isTrusted || !(event.target instanceof Element)) return null;
    const input = event.target.closest(SIZE_QUOTE_INPUT_SELECTOR);
    return input instanceof HTMLInputElement && root.contains(input) ? input : null;
  }

  function isSelectedDiscreteQuoteClick(root, control) {
    if (
      control.getAttribute('aria-pressed') === 'true' ||
      control.classList.contains('is-selected') ||
      control.classList.contains('is-active')
    ) {
      return true;
    }
    if (control.hasAttribute('data-font-option')) {
      const selectedFont = root.querySelector('[data-font-select]');
      return (
        selectedFont instanceof HTMLSelectElement &&
        selectedFont.value === String(control.getAttribute('data-font-option') || '')
      );
    }
    if (control.hasAttribute('data-icon-option')) {
      const selectedIcon = root.querySelector('[data-icon-select]');
      return (
        selectedIcon instanceof HTMLSelectElement &&
        selectedIcon.value === String(control.getAttribute('data-icon-option') || '')
      );
    }
    return false;
  }

  function markDiscreteQuoteCommit(root, event, selector, ignoreSelectedClick = false) {
    if (!event?.isTrusted || !(event.target instanceof Element)) return;
    const control = event.target.closest(selector);
    if (!(control instanceof Element) || !root.contains(control)) return;
    // Selected font, icon, character-color, and mobile-alignment buttons may
    // emit no final designer revision when clicked again. Never let that
    // abandoned click promote the next text or slider update to immediate.
    if (ignoreSelectedClick && isSelectedDiscreteQuoteClick(root, control)) return;
    const context = liveQuoteState(root);
    context.discreteQuoteCommitPending = true;
    context.discreteQuoteCommitSequence += 1;
    const sequence = context.discreteQuoteCommitSequence;
    if (context.discreteQuoteCommitTimer && typeof window.clearTimeout === 'function') {
      window.clearTimeout(context.discreteQuoteCommitTimer);
    }
    // Font activation may wait for its font face before the final designer
    // revision is emitted. Keep the marker across that wait, but never allow
    // an abandoned click to promote an unrelated later edit to immediate.
    context.discreteQuoteCommitTimer = window.setTimeout(() => {
      if (context.discreteQuoteCommitSequence !== sequence) return;
      context.discreteQuoteCommitPending = false;
      context.discreteQuoteCommitTimer = 0;
    }, DISCRETE_QUOTE_COMMIT_TTL_MS);
  }

  function clearDiscreteQuoteCommit(context) {
    if (!context.discreteQuoteCommitPending && !context.discreteQuoteCommitTimer) return;
    context.discreteQuoteCommitPending = false;
    context.discreteQuoteCommitSequence += 1;
    if (context.discreteQuoteCommitTimer && typeof window.clearTimeout === 'function') {
      window.clearTimeout(context.discreteQuoteCommitTimer);
    }
    context.discreteQuoteCommitTimer = 0;
  }

  function consumeDiscreteQuoteCommit(context) {
    const pending = context.discreteQuoteCommitPending;
    clearDiscreteQuoteCommit(context);
    return pending;
  }

  function clearAbandonedDiscreteQuoteCommit(root, event) {
    if (!event?.isTrusted || !(event.target instanceof Element)) return;
    const input = event.target.closest(CONTINUOUS_QUOTE_INPUT_SELECTOR);
    if (!(input instanceof Element) || !root.contains(input)) return;
    clearDiscreteQuoteCommit(liveQuoteState(root));
  }

  function reviewCommitState(root) {
    let state = REVIEW_COMMIT_STATES.get(root);
    if (!state) {
      state = { generation: 0, activeGeneration: 0 };
      REVIEW_COMMIT_STATES.set(root, state);
    }
    return state;
  }

  function beginReviewCommit(root) {
    const state = reviewCommitState(root);
    state.generation += 1;
    state.activeGeneration = state.generation;
    return state.activeGeneration;
  }

  function cancelReviewCommit(root) {
    reviewCommitState(root).activeGeneration = 0;
  }

  function reviewCommitIsActive(root, preparation) {
    return (
      Number.isSafeInteger(preparation?.reviewGeneration) &&
      preparation.reviewGeneration > 0 &&
      reviewCommitState(root).activeGeneration === preparation.reviewGeneration
    );
  }

  function isReviewBackClick(root, event) {
    if (!(event?.target instanceof Element)) return false;
    const button = event.target.closest('[data-review-back]');
    return button instanceof Element && root.contains(button);
  }

  function beginSizeInteraction(root, event, kind) {
    if (!trustedSizeInput(root, event)) return;
    const context = liveQuoteState(root);
    context.sizeInteractionActive = true;
    context.sizeInteractionKind = kind;
  }

  function markSizeInteractionDirty(root, event) {
    if (!trustedSizeInput(root, event)) return;
    const context = liveQuoteState(root);
    context.sizeInteractionActive = true;
    context.sizeInteractionDirty = true;
    if (!context.sizeInteractionKind) context.sizeInteractionKind = 'input';
    context.sizeCommitPending = false;
    if (context.sizeCommitFrame && typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(context.sizeCommitFrame);
      context.sizeCommitFrame = 0;
    }
    clearLiveQuoteTimer(context);
  }

  function flushDeferredSizeQuote(root) {
    const context = liveQuoteState(root);
    context.sizeCommitFrame = 0;
    if (!context.sizeCommitPending || context.sizeInteractionActive) return;
    context.sizeCommitPending = false;
    const deferred = context.deferredSizeQuote;
    context.deferredSizeQuote = null;
    if (!deferred) return;
    scheduleLiveQuote(root, deferred.state, deferred.revision, { immediate: true });
  }

  function finishSizeInteraction(root, event) {
    if (!trustedSizeInput(root, event)) return;
    const context = liveQuoteState(root);
    if (!context.sizeInteractionActive || !context.sizeInteractionDirty) {
      context.sizeInteractionActive = false;
      context.sizeInteractionKind = '';
      return;
    }
    context.sizeInteractionActive = false;
    context.sizeInteractionDirty = false;
    context.sizeInteractionKind = '';
    context.sizeCommitPending = true;
    clearLiveQuoteTimer(context);
    if (context.sizeCommitFrame) return;
    context.sizeCommitFrame = window.requestAnimationFrame(() => flushDeferredSizeQuote(root));
  }

  function installLiveQuoteListener(root) {
    if (root.dataset.liveQuoteInitialized === 'true') return;
    root.dataset.liveQuoteInitialized = 'true';
    root.addEventListener(
      'pointerdown',
      (event) => beginSizeInteraction(root, event, 'pointer'),
      true
    );
    root.addEventListener(
      'keydown',
      (event) => {
        if (SIZE_COMMIT_KEYS.has(event.key)) beginSizeInteraction(root, event, 'keyboard');
      },
      true
    );
    root.addEventListener(
      'input',
      (event) => {
        clearAbandonedDiscreteQuoteCommit(root, event);
        markSizeInteractionDirty(root, event);
      },
      true
    );
    root.addEventListener(
      'click',
      (event) => {
        if (isReviewBackClick(root, event)) cancelReviewCommit(root);
        markDiscreteQuoteCommit(root, event, DISCRETE_QUOTE_CLICK_SELECTOR, true);
      },
      true
    );
    root.addEventListener(
      'change',
      (event) => {
        markDiscreteQuoteCommit(root, event, DISCRETE_QUOTE_CHANGE_SELECTOR);
        const context = liveQuoteState(root);
        if (context.sizeInteractionKind !== 'keyboard') finishSizeInteraction(root, event);
      },
      true
    );
    root.addEventListener('pointerup', (event) => finishSizeInteraction(root, event), true);
    root.addEventListener('pointercancel', (event) => finishSizeInteraction(root, event), true);
    root.addEventListener('touchend', (event) => finishSizeInteraction(root, event), true);
    root.addEventListener(
      'keyup',
      (event) => {
        if (SIZE_COMMIT_KEYS.has(event.key)) finishSizeInteraction(root, event);
      },
      true
    );
    root.addEventListener('focusout', (event) => finishSizeInteraction(root, event), true);
    root.addEventListener('tan:designer:change', (event) => {
      cancelReviewCommit(root);
      const detail = event && typeof event.detail === 'object' ? event.detail : {};
      const state = detail.state;
      const revision = String(
        detail.revision ?? root.dataset.designRevision ?? ''
      );
      const context = liveQuoteState(root);
      const discreteQuoteCommitPending = consumeDiscreteQuoteCommit(context);
      if (context.sizeCommitPending) {
        context.sizeCommitPending = false;
        context.deferredSizeQuote = null;
        if (context.sizeCommitFrame && typeof window.cancelAnimationFrame === 'function') {
          window.cancelAnimationFrame(context.sizeCommitFrame);
          context.sizeCommitFrame = 0;
        }
        scheduleLiveQuote(root, state, revision, { immediate: true });
        return;
      }
      if (discreteQuoteCommitPending) {
        scheduleLiveQuote(root, state, revision, {
          delayMs: DISCRETE_QUOTE_COALESCE_MS,
        });
        return;
      }
      scheduleLiveQuote(root, state, revision, {
        deferRequest: context.sizeInteractionActive && context.sizeInteractionDirty,
      });
    });
  }

  async function quoteNow(root, state, revision) {
    const context = liveQuoteState(root);
    const design = canonicalDesign(root, state);
    const key = canonicalKey(root, design);
    const current = context.currentQuote;
    if (
      current &&
      current.keys.has(key) &&
      quoteIsFresh(current)
    ) {
      if (!applyAuthoritativeQuote(root, state, current.quote, revision)) {
        throw bridgeError('errors.price_changed', 'DESIGN_CHANGED_DURING_QUOTE');
      }
      setExactQuoteStatus(root, 'ready');
      return current.quote;
    }

    if (!isCurrentQuoteTarget(context, key, revision)) {
      markQuoteTarget(root, context, state, key, revision);
    } else {
      clearLiveQuoteTimer(context);
      setExactQuoteStatus(root, 'pending');
    }
    const quote = await resolveQuoteTarget(root, state, design, key, revision);
    if (!context.currentQuote || context.currentQuote.quote !== quote) {
      throw bridgeError('errors.price_changed', 'DESIGN_CHANGED_DURING_QUOTE');
    }
    return quote;
  }

  function moneyFormatter(root, currencyCode) {
    try {
      return new Intl.NumberFormat(root.dataset.locale || 'en', {
        style: 'currency',
        currency: currencyCode,
      });
    } catch {
      return new Intl.NumberFormat('en', { style: 'currency', currency: currencyCode });
    }
  }

  function quoteMoney(root, quote, key) {
    const amountMinor = Number(quote?.[key]?.amountMinor);
    const currencyCode = String(quote?.[key]?.currencyCode || '');
    if (!Number.isSafeInteger(amountMinor) || amountMinor < 0 || !/^[A-Z]{3}$/.test(currencyCode)) {
      throw bridgeError('errors.exact_price_invalid', 'INVALID_EXACT_PRICE');
    }
    const fractionDigits = moneyFormatter(root, currencyCode).resolvedOptions()
      .maximumFractionDigits;
    if (!Number.isInteger(fractionDigits) || fractionDigits < 0 || fractionDigits > 4) {
      throw bridgeError('errors.exact_price_invalid', 'INVALID_EXACT_PRICE');
    }
    const scale = 10 ** fractionDigits;
    return { amountMinor, amount: amountMinor / scale, currencyCode };
  }

  function replaceMeasurementValues(output, centimeters) {
    if (!output) return;
    const replacements = [Math.round(centimeters), Math.round(centimeters / 2.54)];
    let replacementIndex = 0;
    const current = String(output.textContent || '');
    const next = current.replace(/\d+(?:[.,]\d+)?/g, (value) => {
      if (replacementIndex >= replacements.length) return value;
      const replacement = String(replacements[replacementIndex]);
      replacementIndex += 1;
      return replacement;
    });
    // The theme has already rendered localized unit labels at this point. Keep
    // those labels intact instead of introducing fixed bridge copy.
    if (replacementIndex === replacements.length && current !== next) {
      output.textContent = next;
    }
  }

  function quoteResolvedSize(quote) {
    const width = Number(quote?.resolvedSize?.widthCm);
    const height = Number(quote?.resolvedSize?.heightCm);
    const minimumWidth = Number(quote?.resolvedSize?.minimumWidthCm);
    if (
      !Number.isInteger(width) ||
      !Number.isInteger(height) ||
      !Number.isInteger(minimumWidth) ||
      width < 1 ||
      width > 240 ||
      height < 1 ||
      height > 120 ||
      minimumWidth < 1 ||
      minimumWidth > width
    ) {
      throw bridgeError('errors.exact_price_invalid', 'INVALID_EXACT_PRICE');
    }
    return {
      widthCm: width,
      heightCm: height,
      minimumWidthCm: minimumWidth,
    };
  }

  function applyResolvedSize(root, state, quote, revision) {
    const {
      widthCm: width,
      heightCm: height,
      minimumWidthCm: minimumWidth,
    } = quoteResolvedSize(quote);

    if (typeof root.commitAuthoritativeSize === 'function') {
      const committed = root.commitAuthoritativeSize(
        {
          widthCm: width,
          heightCm: height,
          minimumWidthCm: minimumWidth,
        },
        revision
      );
      // The theme flushes a queued customer edit before comparing revisions.
      // A false result means this quote became stale during that flush, so no
      // bridge-owned state, DOM, price, or readiness mutation may follow.
      if (committed === false) return false;
    }

    state.width = width;
    state.height = height;
    state.minimumWidth = minimumWidth;

    root.querySelectorAll('[data-width-range]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.value = String(Math.round(width));
    });
    const usesInches = root.dataset.measurementUnit === 'in';
    const fieldValue = (centimeters) => {
      if (!usesInches) return String(Math.round(centimeters));
      const inches = Math.round((centimeters / 2.54) * 10) / 10;
      return Number.isInteger(inches) ? String(inches) : inches.toFixed(1);
    };
    root.querySelectorAll('[data-width-input]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.value = fieldValue(width);
    });
    root.querySelectorAll('[data-height-input]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.value = fieldValue(height);
    });
    replaceMeasurementValues(root.querySelector('[data-canvas-width-output]'), width);
    replaceMeasurementValues(root.querySelector('[data-width-measure]'), width);
    replaceMeasurementValues(root.querySelector('[data-canvas-height-output]'), height);
    replaceMeasurementValues(root.querySelector('[data-height-measure]'), height);
    return true;
  }

  function renderExactPriceOutputs(root, quote) {
    const exact = quoteMoney(root, quote, 'price');
    const compareAt = quoteMoney(root, quote, 'compareAtPrice');
    if (
      compareAt.currencyCode !== exact.currencyCode ||
      compareAt.amountMinor < exact.amountMinor
    ) {
      throw bridgeError('errors.exact_price_invalid', 'INVALID_EXACT_PRICE');
    }

    const formatter = moneyFormatter(root, exact.currencyCode);
    const formatted = formatter.format(exact.amount);
    const formattedCompareAt = formatter.format(compareAt.amount);
    const showCompareAt = compareAt.amountMinor > exact.amountMinor;
    root
      .querySelectorAll('[data-price-output], [data-canvas-price-output], [data-review-price]')
      .forEach((output) => {
        output.textContent = formatted;
      });
    root
      .querySelectorAll('[data-list-price], [data-canvas-list-price], [data-review-list-price]')
      .forEach((output) => {
        output.textContent = formattedCompareAt;
        output.hidden = !showCompareAt;
      });
    const sizeOutput = root.querySelector('[data-canvas-size-price]');
    if (sizeOutput) sizeOutput.textContent = copy(root, 'summary.exact_total', { price: formatted });

    return { exact, compareAt, showCompareAt };
  }

  function applyAuthoritativeQuote(root, state, quote, revision) {
    const activeCountry = buyerCountryCode(root);
    const quoteCountry = String(quote?.countryCode || '')
      .trim()
      .toUpperCase();
    if (activeCountry && quoteCountry !== activeCountry) {
      throw bridgeError('errors.exact_price_invalid', 'QUOTE_COUNTRY_MISMATCH');
    }
    if (!applyResolvedSize(root, state, quote, revision)) return false;
    state.authoritativeQuote = quote;
    const { exact, compareAt, showCompareAt } = renderExactPriceOutputs(root, quote);

    if (state.price) {
      if (normalized(root.dataset.shopCurrency) === normalized(exact.currencyCode)) {
        state.price.listShop = compareAt.amount;
        state.price.totalShop = exact.amount;
      }
      if (normalized(root.dataset.currency) === normalized(exact.currencyCode)) {
        state.price.listCurrent = compareAt.amount;
        state.price.totalCurrent = exact.amount;
        state.price.totalMinor = exact.amountMinor;
        state.price.discountPercent = showCompareAt
          ? Math.max(
              Number(state.price.discountPercent) || 0,
              ((compareAt.amountMinor - exact.amountMinor) / compareAt.amountMinor) * 100
            )
          : 0;
      }
    }
    return true;
  }

  function preparationMatches(root, state, preparation) {
    if (!preparation || preparation.revision !== String(root.dataset.designRevision || '')) {
      return false;
    }
    try {
      return preparation.keys.has(canonicalKey(root, canonicalDesign(root, state)));
    } catch {
      return false;
    }
  }

  function readyDesignRequests(root) {
    if (!READY_DESIGN_REQUESTS.has(root)) {
      READY_DESIGN_REQUESTS.set(root, new Map());
    }
    return READY_DESIGN_REQUESTS.get(root);
  }

  function trimReadyDesignRequests(requests) {
    while (requests.size > READY_DESIGN_REQUEST_LIMIT) {
      const removable = Array.from(requests.entries()).find(
        ([, entry]) => entry.status === 'ready'
      );
      if (!removable) return;
      requests.delete(removable[0]);
    }
  }

  function readyDesignQuoteIsFresh(ready) {
    return quoteIsFresh({
      quote: ready?.quote,
      expiresAt:
        ready && typeof ready === 'object'
          ? Number(READY_DESIGN_EXPIRIES.get(ready) || 0)
          : 0,
    });
  }

  async function refreshReadyDesign(root, ready) {
    const designId = String(ready?.designId || '');
    if (!designId) {
      throw bridgeError('errors.prepare_product_failed', 'INVALID_DESIGN_ID');
    }
    return waitForReady(
      root,
      await apiRequest(root, `designs/${encodeURIComponent(designId)}`)
    );
  }

  function validateReadyDesign(root, ready) {
    const designId = String(ready?.designId || '');
    const activeCountry = buyerCountryCode(root);
    const quoteCountry = String(ready?.quote?.countryCode || '')
      .trim()
      .toUpperCase();
    if (!designId || (activeCountry && quoteCountry !== activeCountry)) {
      throw bridgeError('errors.exact_price_invalid', 'INVALID_READY_DESIGN');
    }
    quoteResolvedSize(ready.quote);
    const exact = quoteMoney(root, ready.quote, 'price');
    const compareAt = quoteMoney(root, ready.quote, 'compareAtPrice');
    if (
      compareAt.currencyCode !== exact.currencyCode ||
      compareAt.amountMinor < exact.amountMinor
    ) {
      throw bridgeError('errors.exact_price_invalid', 'INVALID_READY_DESIGN');
    }
    const variantId = Number(ready?.cart?.variantId);
    if (!Number.isSafeInteger(variantId) || variantId <= 0) {
      throw bridgeError('errors.invalid_variant', 'INVALID_DESIGN_VARIANT');
    }
    return ready;
  }

  function requestReadyDesign(root, design, key) {
    const requests = readyDesignRequests(root);
    const existing = requests.get(key);
    if (
      existing &&
      (existing.status === 'pending' || readyDesignQuoteIsFresh(existing.ready))
    ) {
      requests.delete(key);
      requests.set(key, existing);
      return existing.promise;
    }
    const entry = { promise: null, status: 'pending' };
    const pending = (
      existing
        ? refreshReadyDesign(root, existing.ready)
        : createReadyDesign(root, design)
    ).then((ready) => {
      const validated = validateReadyDesign(root, ready);
      READY_DESIGN_EXPIRIES.set(
        validated,
        Date.now() + READY_DESIGN_QUOTE_TTL_MS
      );
      return validated;
    });
    entry.promise = pending;
    requests.set(key, entry);
    // A rejected request must be retryable and must remain handled even if the
    // customer leaves review before anything awaits the preparation entry.
    void pending.then(
      (ready) => {
        entry.ready = ready;
        entry.status = 'ready';
        trimReadyDesignRequests(requests);
      },
      () => {
        if (requests.get(key) === entry) requests.delete(key);
      }
    );
    trimReadyDesignRequests(requests);
    return pending;
  }

  function commitPreparedQuote(root, state, preparation, ready) {
    if (!preparationMatches(root, state, preparation)) {
      throw bridgeError('errors.price_changed', 'DESIGN_CHANGED_DURING_QUOTE');
    }
    const quote = ready.quote;
    if (!applyAuthoritativeQuote(root, state, quote, preparation.revision)) {
      throw bridgeError('errors.price_changed', 'DESIGN_CHANGED_DURING_QUOTE');
    }
    if (!preparationMatches(root, state, preparation)) {
      throw bridgeError('errors.price_changed', 'DESIGN_CHANGED_DURING_QUOTE');
    }
    const context = liveQuoteState(root);
    const resolvedKey = resolvedCanonicalKey(root, canonicalDesign(root, state), quote);
    preparation.keys.add(resolvedKey);
    rememberExactPriceDisplay(root, context, quote);
    cacheQuote(context, preparation.keys, quote);
    context.currentKey = resolvedKey;
    context.currentRevision = preparation.revision;
    context.currentState = state;
    context.currentQuote = {
      quote,
      keys: new Set(preparation.keys),
      revision: preparation.revision,
      expiresAt: quoteExpiresAt(quote),
    };
    setExactQuoteStatus(root, 'ready');
    return quote;
  }

  function commitPreparedQuoteWhenReviewOpens(root, state, preparation, ready) {
    const deadline = Date.now() + 2_000;
    return new Promise((resolve, reject) => {
      const commitWhenSafe = () => {
        if (!root.isConnected || !reviewCommitIsActive(root, preparation)) {
          resolve();
          return;
        }
        const reviewTransition = root.dataset.reviewTransition;
        const reviewIsActive =
          root.dataset.designerStep === 'review' &&
          (root.dataset.reviewPreparing === 'true' ||
            reviewTransition === 'entering' ||
            reviewTransition === 'ready');
        if (reviewIsActive) {
          try {
            commitPreparedQuote(root, state, preparation, ready);
            resolve();
          } catch (error) {
            reject(error);
          }
          return;
        }
        if (root.dataset.designerStep === 'review') {
          // Back removes reviewTransition before history/popstate switches the
          // step to edit. Treat that brief bare-review state as abandoned so a
          // completed background request cannot visibly update the old view.
          resolve();
          return;
        }
        const transitionPending =
          root.dataset.reviewPreparing === 'true' ||
          reviewTransition === 'preparing' ||
          reviewTransition === 'entering';
        if (transitionPending && Date.now() < deadline) {
          window.requestAnimationFrame(commitWhenSafe);
          return;
        }
        // The customer returned to editing or otherwise left this transition.
        // Keep the prepared product cached without changing the visible state.
        resolve();
      };
      window.requestAnimationFrame(commitWhenSafe);
    });
  }

  async function finishBackgroundPreparation(root, state, design, preparation) {
    const key = canonicalKey(root, design);
    const ready = await requestReadyDesign(root, design, key);
    const resolvedKey = resolvedCanonicalKey(root, design, ready.quote);
    preparation.keys.add(key);
    preparation.keys.add(resolvedKey);
    if (
      String(root.dataset.designRevision || '') !== preparation.revision ||
      !preparation.keys.has(canonicalKey(root, canonicalDesign(root, state)))
    ) {
      throw bridgeError('errors.price_changed', 'DESIGN_CHANGED_DURING_QUOTE');
    }
    preparation.ready = ready;
    // The theme may intentionally delay the visual review transition on
    // mobile. Wait only for that active transition; an actual Back/edit/leave
    // keeps the ready result silent and cannot change the current UI.
    await commitPreparedQuoteWhenReviewOpens(root, state, preparation, ready);
    return ready;
  }

  async function prepareReview(root, state) {
    try {
      return await prepareReviewRequest(root, state);
    } catch (error) {
      throw customerError(root, error, 'errors.exact_price_failed');
    }
  }

  async function prepareReviewRequest(root, state) {
    const revision = String(root.dataset.designRevision || '');
    const initialDesign = canonicalDesign(root, state);
    const initialKey = canonicalKey(root, initialDesign);
    const active = REVIEW_REQUESTS.get(root);
    if (active && active.revision === revision && active.key === initialKey) {
      return active.promise;
    }

    const reviewGeneration = beginReviewCommit(root);

    const existing = PREPARED_DESIGNS.get(root);
    if (
      existing &&
      preparationMatches(root, state, existing) &&
      existing.ready &&
      readyDesignQuoteIsFresh(existing.ready)
    ) {
      return commitPreparedQuote(root, state, existing, existing.ready);
    }
    const context = liveQuoteState(root);
    const current = context.currentQuote;
    if (
      existing &&
      preparationMatches(root, state, existing) &&
      !existing.ready &&
      current &&
      current.keys.has(initialKey) &&
      quoteIsFresh(current)
    ) {
      existing.reviewGeneration = reviewGeneration;
      return current.quote;
    }

    const request = { promise: null, key: initialKey, revision };
    const pending = (async () => {
      showError(root, '');
      const quote = await quoteNow(root, state, revision);
      const design = canonicalDesign(root, state);
      const key = canonicalKey(root, design);
      const quotedKey = resolvedCanonicalKey(root, initialDesign, quote);
      if (
        String(root.dataset.designRevision || '') !== revision ||
        (key !== initialKey && key !== quotedKey)
      ) {
        throw bridgeError('errors.price_changed', 'DESIGN_CHANGED_DURING_QUOTE');
      }
      const prepared = PREPARED_DESIGNS.get(root);
      if (
        prepared &&
        preparationMatches(root, state, prepared) &&
        (!prepared.ready || readyDesignQuoteIsFresh(prepared.ready))
      ) {
        return quote;
      }

      const preparation = {
        promise: null,
        ready: null,
        keys: new Set([initialKey, key, quotedKey]),
        revision,
        reviewGeneration,
      };
      const background = finishBackgroundPreparation(root, state, design, preparation).catch(
        (error) => {
          throw customerError(root, error, 'errors.prepare_product_failed');
        }
      );
      preparation.promise = background;
      PREPARED_DESIGNS.set(root, preparation);
      // The rejection handler is attached before review opens, so abandoning
      // the page cannot produce an unhandled rejection. A failed current entry
      // is removed, allowing Add to cart (or a later Next) to retry safely.
      void background.then(
        () => {},
        () => {
          if (PREPARED_DESIGNS.get(root) === preparation) {
            PREPARED_DESIGNS.delete(root);
          }
        }
      );
      return quote;
    })();
    request.promise = pending;
    REVIEW_REQUESTS.set(root, request);
    void pending.then(
      () => {
        if (REVIEW_REQUESTS.get(root) === request) REVIEW_REQUESTS.delete(root);
      },
      () => {
        if (REVIEW_REQUESTS.get(root) === request) REVIEW_REQUESTS.delete(root);
      }
    );
    return pending;
  }

  async function fingerprint(value) {
    const source = JSON.stringify(value);
    if (window.crypto?.subtle) {
      const bytes = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(source));
      return Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, '0')).join('');
    }
    let hash = 2166136261;
    for (let index = 0; index < source.length; index += 1) {
      hash ^= source.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
  }

  async function idempotencyKey(design) {
    const key = `tan-designer-idempotency:${await fingerprint(design)}`;
    try {
      const existing = sessionStorage.getItem(key);
      if (existing) return existing;
      const value = window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `tan-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(key, value);
      return value;
    } catch {
      return window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `tan-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
  }

  async function createReadyDesign(root, design) {
    const created = await apiRequest(root, 'designs', {
      method: 'POST',
      headers: { 'Idempotency-Key': await idempotencyKey(design) },
      body: JSON.stringify(design),
    });
    return waitForReady(root, created);
  }

  async function waitForReady(root, created) {
    let current = created;
    for (let attempt = 0; current?.status === 'creating' && attempt < 24; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 750));
      current = await apiRequest(root, `designs/${encodeURIComponent(current.designId)}`);
    }
    if (current?.status === 'failed') {
      throw bridgeError('errors.prepare_product_failed', 'DESIGN_PRODUCT_FAILED');
    }
    if (current?.status !== 'ready' || !current.cart) {
      throw bridgeError('errors.product_still_preparing', 'DESIGN_PRODUCT_NOT_READY');
    }
    return current;
  }

  function readableProperties(state, created) {
    const properties = {
      'Design ID': created.designId,
      Text: String(state.text || ''),
      Font: String(state.fontLabel || state.font?.label || ''),
      'Sign size': `${Math.round(Number(state.width) || 0)} × ${Math.round(Number(state.height) || 0)} cm`,
      Color: String(state.color || ''),
      Usage: String(state.usage || ''),
      Backboard: `${state.backboard || ''} · ${state.backboardColor || ''}`,
      Mounting: String(state.mounting || ''),
      'Remote control': String(state.remote || ''),
      Production: String(state.rush || ''),
      'Mock-up': String(state.mockup || ''),
      'Greeting card': String(state.greeting || ''),
    };
    if (state.characterColorSummary) properties['Character colors'] = String(state.characterColorSummary);
    if (state.icon) {
      properties.Icon = String(state.iconLabel || '');
      properties['Icon position'] = String(state.iconPlacementLabel || state.iconPosition || '');
      properties['Icon size'] = `${Math.round(Number(state.iconScale) || 100)}%`;
    }
    if (state.rushNotes) properties['Event or deadline notes'] = String(state.rushNotes);
    if (state.greetingMessage) properties['Greeting card message'] = String(state.greetingMessage);
    if (state.specialRequest) properties['Special request'] = String(state.specialRequest);
    return properties;
  }

  function setLoading(root, loading) {
    const button = root.querySelector('[data-add-to-cart]');
    const label = root.querySelector('[data-add-label]');
    if (button instanceof HTMLButtonElement) {
      button.dataset.loading = String(loading);
      button.disabled = loading;
    }
    if (label) {
      if (!label.dataset.idleLabel) {
        label.dataset.idleLabel = label.textContent?.trim() || copy(root, 'summary.add_to_cart');
      }
      label.textContent = loading ? copy(root, 'progress.adding') : label.dataset.idleLabel;
    }
  }

  function cartItemHasGeneratedImage(item, designId) {
    if (!item || typeof item !== 'object') return false;
    const properties = item.properties && typeof item.properties === 'object' ? item.properties : {};
    if (String(properties._tan_design_id || '') !== String(designId || '')) return false;
    return Boolean(item.image || item.featured_image?.url || item.featured_image?.src);
  }

  async function waitForCartImage(rootWithSlash, designId, addResult) {
    const addedItems = Array.isArray(addResult?.items) ? addResult.items : [addResult];
    if (addedItems.some((item) => cartItemHasGeneratedImage(item, designId))) return;
    for (let attempt = 0; attempt < 8; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 300));
      try {
        const response = await fetch(`${rootWithSlash}cart.js`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!response.ok) continue;
        const cart = await response.json();
        if (
          Array.isArray(cart?.items) &&
          cart.items.some((item) => cartItemHasGeneratedImage(item, designId))
        ) {
          return;
        }
      } catch {
        // Product media readiness is enforced server-side; this is a best-effort storefront cache check.
      }
    }
  }

  async function addToCart(root, state) {
    const button = root.querySelector('[data-add-to-cart]');
    if (!(button instanceof HTMLButtonElement) || button.dataset.loading === 'true') return;
    setLoading(root, true);
    showError(root, '');
    openCartProgress(root);
    try {
      const revision = String(root.dataset.designRevision || '');
      const design = canonicalDesign(root, state);
      const key = canonicalKey(root, design);
      const prepared = PREPARED_DESIGNS.get(root);
      const usesPrepared = Boolean(
        prepared && prepared.revision === revision && prepared.keys.has(key)
      );
      let ready = usesPrepared
        ? await prepared.promise
        : await requestReadyDesign(root, design, key);
      if (!readyDesignQuoteIsFresh(ready)) {
        ready = await requestReadyDesign(root, design, key);
      }
      const readyKeys = usesPrepared
        ? prepared.keys
        : new Set([key, resolvedCanonicalKey(root, design, ready.quote)]);
      if (
        String(root.dataset.designRevision || '') !== revision ||
        !readyKeys.has(canonicalKey(root, canonicalDesign(root, state)))
      ) {
        throw bridgeError('errors.design_changed', 'DESIGN_CHANGED_BEFORE_CART');
      }
      if (!applyAuthoritativeQuote(root, state, ready.quote, revision)) {
        throw bridgeError('errors.design_changed', 'DESIGN_CHANGED_BEFORE_CART');
      }
      const variantId = Number(ready.cart.variantId);
      if (!Number.isSafeInteger(variantId) || variantId <= 0) {
        throw bridgeError('errors.invalid_variant', 'INVALID_DESIGN_VARIANT');
      }
      const properties = {
        ...readableProperties(state, ready),
        ...ready.cart.properties,
      };
      await holdActiveCartProgress(root);
      setCartProgress(root, 'cart');
      const shopRoot = String(window.Shopify?.routes?.root || '/');
      const rootWithSlash = shopRoot.endsWith('/') ? shopRoot : `${shopRoot}/`;
      const response = await fetch(`${rootWithSlash}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties }],
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw bridgeError('errors.add_to_cart_failed', 'CART_ADD_FAILED');
      }
      await holdActiveCartProgress(root);
      setCartProgress(root, 'preview');
      await waitForCartImage(rootWithSlash, ready.designId, result);
      await holdActiveCartProgress(root);
      completeCartProgress(root);
      await new Promise((resolve) => window.setTimeout(resolve, 220));
      window.location.assign(root.dataset.cartUrl || `${rootWithSlash}cart`);
    } catch (error) {
      const message = customerMessage(root, error, 'errors.add_to_cart_failed');
      showError(root, message);
      failCartProgress(root, message);
      setLoading(root, false);
    }
  }

  window.TANDesignerAppBridge = Object.freeze({ addToCart, prepareReview });

  const roots = () => document.querySelectorAll('[data-tan-neon-designer]');
  const cancelAllReviewCommits = () => roots().forEach((root) => cancelReviewCommit(root));
  window.addEventListener('popstate', cancelAllReviewCommits);
  window.addEventListener('pagehide', cancelAllReviewCommits);
  roots().forEach((root) => void bootstrapRoot(root));
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (!(section instanceof Element)) return;
    section.querySelectorAll('[data-tan-neon-designer]').forEach((root) => void bootstrapRoot(root));
  });
})();
